import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/auth/AuthContext";
import { getAdminUsers, getAdminUsersCount, type AdminUserRow } from "@/api/admin/users";

function formatUserRole(role?: string): string {
  if (role === "SYSTEM_ADMIN") return "Admin";
  if (role === "TEACHER") return "Teacher";
  return "Student";
}

function userDisplayName(u: AdminUserRow): string {
  const fn = u.profile?.firstName ?? "";
  const ln = u.profile?.lastName ?? "";
  const full = `${fn} ${ln}`.trim();
  return full || u.email;
}

export default function AdminUsersPage() {
  // ProtectedRoute already gates by role, but we keep Auth usage consistent.
  useAuth();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const usersQuery = useQuery({
    queryKey: ["adminUsers", page, limit, debouncedSearch],
    queryFn: () => getAdminUsers({ page, limit, search: debouncedSearch }),
  });

  const countQuery = useQuery({
    queryKey: ["adminUsersCount"],
    queryFn: () => getAdminUsersCount(),
  });

  const totalPages = useMemo(() => {
    // If searching, use the returned count instead of total system count.
    // getAdminUsers returns a 'count' field in the response which corresponds to the search results.
    const count = debouncedSearch ? usersQuery.data?.count ?? 0 : countQuery.data?.count ?? 0;
    return Math.max(1, Math.ceil(count / limit));
  }, [countQuery.data, usersQuery.data, limit, debouncedSearch]);

  const filtered = usersQuery.data?.data ?? [];

  return (
    <DashboardLayout role="admin">
      <div className="dash-page">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">All Users</h1>
            <p className="dash-page-subtitle">Browse and manage platform users.</p>
          </div>
          <div className="w-full sm:w-[260px]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
            />
          </div>
        </div>

        <KCard className="overflow-hidden">
          {usersQuery.isPending || countQuery.isPending ? (
            <div className="dash-empty-state">
              <Loader2 className="animate-spin mb-2" size={24} />
              <span className="text-sm font-medium">Loading users…</span>
            </div>
          ) : usersQuery.isError ? (
            <div className="dash-empty-state">
              <p className="text-sm font-medium text-destructive">Failed to load users.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full dash-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-4">
                        <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                          <p className="text-sm font-medium">No users found.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <p className="text-sm font-medium text-foreground leading-tight">
                            {userDisplayName(u)}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{u.email}</p>
                        </td>
                        <td>
                          <span className={`dash-badge ${
                            u.role === "TEACHER"
                              ? "bg-primary/10 text-primary"
                              : u.role === "SYSTEM_ADMIN"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-muted text-muted-foreground"
                          }`}>
                            {formatUserRole(u.role)}
                          </span>
                        </td>
                        <td className="text-[11px] text-muted-foreground">
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                        </td>
                        <td>
                          <button
                            onClick={() => navigate(`/admin/students/${u.id}/analytics`)}
                            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-lg hover:bg-primary/5"
                          >
                            View Analytics
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-4 py-3 border-t border-border flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || usersQuery.isPending}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || usersQuery.isPending}
              >
                Next
              </Button>
            </div>
          </div>
        </KCard>
      </div>
    </DashboardLayout>
  );
}

